import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Youtube, UploadCloud, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
import { useVerification } from '../../hooks/useVerification';
import VerifyModal from '../common/VerifyModal';
import { VIDEO_CATEGORIES } from '../../lib/videoLibrary';
import {
  callCfStream,
  uploadToCloudflare,
  pollUntilReady,
  readVideoDuration,
  formatBytes,
  MEMBER_MAX_BYTES,
  MEMBER_MAX_DURATION_SECONDS,
} from '../../lib/streamUpload';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const CATEGORIES = VIDEO_CATEGORIES;

// mp4 and QuickTime only, matching CuratedVideosScreen. Validated on file.type,
// never on the filename — an extension is a claim, a MIME type is at least the
// browser's own reading of the file.
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

const extractYoutubeId = (url) => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

const SubmitVideoScreen = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const { showVerifyModal, setShowVerifyModal, requireVerified } = useVerification();

  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null); // { youtubeId, title, thumbnailUrl }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [commentary, setCommentary] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // ── Upload mode (043) ──────────────────────────────────────────────
  // 'upload' sends a file to Cloudflare Stream; 'youtube' is the original flow,
  // untouched. Upload is the DEFAULT: sharing your own footage is the thing
  // this screen was reworked for, and a YouTube link is the fallback.
  const [mode, setMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('');        // user-facing step label
  const [stillProcessing, setStillProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const uploadRef = useRef(null);                // in-flight XHR, for Cancel

  const handlePickFile = async (picked) => {
    setFileError('');
    setFile(null);
    if (!picked) return;

    if (!ALLOWED_VIDEO_TYPES.includes(picked.type)) {
      setFileError('Please choose an MP4 or MOV file.');
      return;
    }
    if (picked.size > MEMBER_MAX_BYTES) {
      setFileError(
        `That file is ${formatBytes(picked.size)}. The limit is ${formatBytes(MEMBER_MAX_BYTES)} — ` +
        'try a shorter clip, or export it at a lower resolution.'
      );
      return;
    }
    // Checked here only to fail fast with a clear message. Cloudflare enforces
    // the real ceiling at ingest, because anything the browser measures can be
    // lied about.
    const duration = await readVideoDuration(picked);
    if (duration && duration > MEMBER_MAX_DURATION_SECONDS) {
      setFileError(
        `That video is ${Math.round(duration / 60)} minutes. The limit is ` +
        `${MEMBER_MAX_DURATION_SECONDS / 60} minutes.`
      );
      return;
    }
    setFile(picked);
  };

  const uploadErrorMessage = (err) => {
    const msg = err?.message || '';
    if (msg.includes('quota_exceeded')) {
      return 'You have reached the limit of 3 video uploads per week. You can still share a YouTube link.';
    }
    if (msg.includes('not_verified')) return 'You need to be a verified DSP to upload videos.';
    if (msg.includes('too_many_drafts')) {
      return 'You have several unfinished uploads. Try again in a couple of hours.';
    }
    if (msg === 'aborted') return '';
    return msg || 'Something went wrong. Please try again.';
  };

  const handleUploadSubmit = async () => {
    if (!requireVerified()) return;
    if (!file || !category || !postTitle.trim()) return;

    setSubmitError('');
    setSubmitting(true);
    setUploadPct(0);
    setStillProcessing(false);

    let submissionId = null;
    try {
      // The row comes FIRST: it is what the quota counts and what cf-stream
      // checks ownership against before it will mint anything.
      setPhase('Preparing…');
      const { data: newId, error: createError } = await supabase.rpc('create_upload_submission', {
        p_title: postTitle.trim(),
        p_commentary: commentary.trim() || null,
        p_category: category,
      });
      if (createError) throw new Error(createError.message);
      submissionId = newId;

      setPhase('Getting an upload link…');
      const { uploadURL, uid } = await callCfStream({
        action: 'create_upload',
        submission_id: submissionId,
      });

      setPhase('Uploading…');
      const upload = uploadToCloudflare(uploadURL, file, setUploadPct);
      uploadRef.current = upload;
      await upload;
      uploadRef.current = null;

      // Cloudflare transcodes before anything is playable. A timeout here is
      // NOT a failure — the row exists and any later poll reconciles it — so
      // the member is told it is still processing rather than that it broke.
      setPhase('Processing…');
      const ready = await pollUntilReady(uid, { submissionId });
      if (!ready) setStillProcessing(true);

      setSubmitted(true);
    } catch (err) {
      uploadRef.current = null;
      // Clean up so an abandoned attempt neither blocks the member's quota nor
      // leaves an asset billing: cancel_upload_submission deletes the row
      // outright if nothing was minted, and queues the asset for deletion if it
      // was.
      if (submissionId) {
        // try/catch, not .catch(): a PostgrestBuilder is only PromiseLike.
        try { await supabase.rpc('cancel_upload_submission', { p_id: submissionId }); } catch { /* best effort */ }
      }
      setSubmitError(uploadErrorMessage(err));
    } finally {
      setSubmitting(false);
      setPhase('');
    }
  };

  const handleCancelUpload = () => {
    if (uploadRef.current?.abort) uploadRef.current.abort();
  };

  const fetchPreview = async (inputUrl) => {
    const youtubeId = extractYoutubeId(inputUrl.trim());
    if (!youtubeId) {
      setPreviewError('Paste a valid YouTube URL (youtube.com or youtu.be)');
      setPreview(null);
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreview(null);

    try {
      const res = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${youtubeId}&format=json`
      );
      if (!res.ok) {
        setPreviewError('Video not found or is private. Check the URL and try again.');
        setPreviewLoading(false);
        return;
      }
      const data = await res.json();
      setPreview({
        youtubeId,
        title: data.title,
        thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
      });
    } catch {
      setPreviewError('Could not load video info. Check the URL and try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUrlBlur = () => {
    if (url.trim()) fetchPreview(url);
  };

  const handleUrlKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (url.trim()) fetchPreview(url);
    }
  };

  const handleSubmit = async () => {
    if (!requireVerified()) return;
    if (!preview || !category) return;

    setSubmitError('');
    setSubmitting(true);

    const { error } = await supabase.rpc('submit_video', {
      p_youtube_url: url.trim(),
      p_youtube_id: preview.youtubeId,
      p_title: preview.title,
      p_thumbnail_url: preview.thumbnailUrl,
      p_commentary: commentary.trim() || null,
      p_category: category,
      p_post_title: postTitle.trim() || null,
    });

    setSubmitting(false);

    if (error) {
      if (error.message?.includes('duplicate_video')) {
        setSubmitError('This video has already been submitted.');
      } else if (error.message?.includes('not_verified')) {
        setSubmitError('You need to be a verified DSP to submit videos.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
      return;
    }

    setSubmitted(true);
  };

  // Gate: non-verified users see a prompt instead of the form
  if (!userData?.verified) {
    return (
      <div className="min-h-screen pb-nav">
        {/* Mobile header */}
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <button
              onClick={() => navigate('/explore/videos')}
              className="flex items-center gap-1 mb-3 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <h1 className="text-xl font-bold text-white tracking-tight">Submit a Video</h1>
          </div>
        </div>
        {/* Desktop heading */}
        <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Submit a Video</h2>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8 text-center">
          <div className="rounded-xl p-8" style={cardStyle}>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `rgba(var(--brand-accent-rgb), 0.15)` }}
            >
              <AlertCircle size={28} style={{ color: 'var(--brand-accent)' }} />
            </div>
            <h3 className="text-lg font-bold mb-2 text-primary">Verification required</h3>
            <p className="text-sm text-secondary mb-6 leading-relaxed">
              Only verified DSPs can submit videos to the community library. Get verified to share resources with your peers.
            </p>
            <button
              onClick={() => requireVerified()}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Get Verified
            </button>
          </div>
        </div>

        {showVerifyModal && <VerifyModal onClose={() => setShowVerifyModal(false)} />}
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen pb-nav">
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <div className="h-6 mb-3" />
            <h1 className="text-xl font-bold text-white tracking-tight">Submit a Video</h1>
          </div>
        </div>
        <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Submit a Video</h2>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="rounded-xl p-8" style={cardStyle}>
            <CheckCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
            <h3 className="text-xl font-bold mb-2 text-primary">Submitted for review!</h3>
            <p className="text-sm text-secondary mb-6 leading-relaxed">
              {stillProcessing
                ? 'Your video is uploaded and still being processed. It will reach the moderation queue on its own — you do not need to do anything else.'
                : 'Your video has been sent to the moderation queue. A community moderator will review it shortly.'}
            </p>
            <button
              onClick={() => navigate('/explore/videos')}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Back to Video Library
            </button>
            <button
              onClick={() => {
                setUrl('');
                setPreview(null);
                setPostTitle('');
                setCommentary('');
                setCategory('');
                setSubmitted(false);
                setFile(null);
                setFileError('');
                setUploadPct(0);
                setStillProcessing(false);
              }}
              className="w-full py-3 rounded-xl font-medium text-sm mt-2 transition-all hover-surface"
              style={{ color: 'var(--text-secondary)' }}
            >
              Submit another video
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isUpload = mode === 'upload';
  // A YouTube submission borrows the video's own title, so Post Title is
  // optional there. An uploaded file has no title but the one the member types,
  // so it is required — and it is the same field, not a second one.
  const canSubmit = isUpload
    ? Boolean(file && category && postTitle.trim() && !submitting)
    : Boolean(preview && category && !submitting);

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile: sticky brand header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <button
            onClick={() => navigate('/explore/videos')}
            className="flex items-center gap-1 mb-3 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <h1 className="text-xl font-bold text-white tracking-tight">Submit a Video</h1>
        </div>
      </div>
      {/* Desktop heading */}
      <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
        <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Submit a Video</h2>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

        {/* Source chooser (043) */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'youtube', label: 'YouTube link', Icon: Youtube },
            { key: 'upload',  label: 'Upload a video', Icon: UploadCloud },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => { setMode(key); setSubmitError(''); }}
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
              style={{
                backgroundColor: mode === key ? 'var(--brand-primary)' : 'var(--card-bg)',
                color: mode === key ? '#fff' : 'var(--text-secondary)',
                border: '1px solid ' + (mode === key ? 'var(--brand-primary)' : 'var(--card-border)'),
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* YouTube mode only: the URL comes first, because the title preview and
            the thumbnail are both derived from it. */}
        {!isUpload && (
        <div className="rounded-xl p-4 space-y-3" style={cardStyle}>
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-primary">YouTube URL</label>
            <p className="text-xs text-tertiary mb-2">Paste a link to a YouTube video relevant to DSP work.</p>
            <input
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setPreview(null); setPreviewError(''); }}
              onBlur={handleUrlBlur}
              onKeyDown={handleUrlKeyDown}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                backgroundColor: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Preview loading */}
          {previewLoading && (
            <div className="flex items-center gap-2 py-2">
              <span className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin flex-shrink-0"
                style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
              <span className="text-xs text-secondary">Loading video info...</span>
            </div>
          )}

          {/* Preview error */}
          {previewError && (
            <p className="text-xs" style={{ color: '#F87171' }}>{previewError}</p>
          )}

          {/* Preview card */}
          {preview && (
            <div
              className="flex gap-3 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
            >
              <div className="w-24 flex-shrink-0 rounded-md overflow-hidden aspect-video bg-black">
                <img src={preview.thumbnailUrl} alt={preview.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primary line-clamp-2 leading-snug">{preview.title}</p>
                <p className="text-xs text-tertiary mt-1">youtube.com</p>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Title */}
        <div className="rounded-xl p-4" style={cardStyle}>
          <label className="block text-sm font-semibold mb-1.5 text-primary">
            {isUpload ? 'Title' : 'Post Title'}{' '}
            {isUpload
              ? <span style={{ color: '#F87171' }}>*</span>
              : <span className="font-normal text-tertiary">(optional)</span>}
          </label>
          <p className="text-xs text-tertiary mb-2">
            {isUpload
              ? 'What would you like your video to be titled in the library?'
              : 'Give the video a title that speaks to DSPs — replaces the YouTube title in the library.'}
          </p>
          <input
            type="text"
            value={postTitle}
            onChange={(e) => setPostTitle(e.target.value.slice(0, 80))}
            placeholder="e.g. How I handle meltdowns without burning out"
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={{
              backgroundColor: 'var(--input-bg)',
              border: '1px solid var(--card-border)',
              color: 'var(--text-primary)',
            }}
          />
          <p className="text-right text-xs mt-1 text-tertiary">{postTitle.length}/80</p>
        </div>

        {/* Category */}
        <div className="rounded-xl p-4" style={cardStyle}>
          <label className="block text-sm font-semibold mb-3 text-primary">Category <span style={{ color: '#F87171' }}>*</span></label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: category === cat ? 'var(--brand-primary)' : 'var(--input-bg)',
                  color: category === cat ? '#fff' : 'var(--text-secondary)',
                  border: '1px solid ' + (category === cat ? 'var(--brand-primary)' : 'var(--card-border)'),
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Why are you sharing this? */}
        <div className="rounded-xl p-4" style={cardStyle}>
          <label className="block text-sm font-semibold mb-1.5 text-primary">
            Why are you sharing this? <span className="font-normal text-tertiary">(optional)</span>
          </label>
          <p className="text-xs text-tertiary mb-2">
            Why are you sharing it? Add context for the community — what makes this video worth watching?
          </p>
          <textarea
            value={commentary}
            onChange={(e) => setCommentary(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="e.g. Great breakdown of de-escalation techniques I use every shift..."
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
            style={{
              backgroundColor: 'var(--input-bg)',
              border: '1px solid var(--card-border)',
              color: 'var(--text-primary)',
            }}
          />
          <p className="text-right text-xs mt-1 text-tertiary">{commentary.length}/500</p>
        </div>

        {/* Upload mode only: the file picker is LAST, so the member describes the
            video before committing to a long upload — and so the consent notice
            sits immediately above the button that starts it. */}
        {isUpload && (
          <div className="rounded-xl p-4 space-y-3" style={cardStyle}>
            <label className="block text-sm font-semibold mb-1.5 text-primary">
              Your video file <span style={{ color: '#F87171' }}>*</span>
            </label>
            <p className="text-xs text-tertiary mb-2">
              MP4 or MOV, up to {formatBytes(MEMBER_MAX_BYTES)} and{' '}
              {MEMBER_MAX_DURATION_SECONDS / 60} minutes. Up to 3 uploads a week.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime"
              className="hidden"
              onChange={(e) => handlePickFile(e.target.files?.[0] ?? null)}
            />

            {!file ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                className="w-full py-6 rounded-lg text-sm font-medium flex flex-col items-center gap-2 transition-all hover-surface disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px dashed var(--card-border)',
                  color: 'var(--text-secondary)',
                }}
              >
                <UploadCloud size={22} style={{ color: 'var(--brand-primary)' }} />
                Choose a video
              </button>
            ) : (
              <div
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary truncate">{file.name}</p>
                  <p className="text-xs text-tertiary mt-0.5">{formatBytes(file.size)}</p>
                </div>
                {!submitting && (
                  <button
                    onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    aria-label="Remove this video"
                    className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {fileError && <p className="text-xs" style={{ color: '#F87171' }}>{fileError}</p>}

            {/* Progress. The bytes go straight to Cloudflare, so this is real. */}
            {submitting && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-secondary">
                  <span>{phase}</span>
                  {uploadPct > 0 && uploadPct < 100 && <span>{uploadPct}%</span>}
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--input-bg)' }}>
                  <div
                    className="h-full transition-all duration-300"
                    style={{ width: `${uploadPct || 4}%`, backgroundColor: 'var(--brand-primary)' }}
                  />
                </div>
                {uploadPct > 0 && uploadPct < 100 && (
                  <button
                    onClick={handleCancelUpload}
                    className="text-xs font-medium transition-colors"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    Cancel upload
                  </button>
                )}
              </div>
            )}

            <p className="text-xs text-tertiary leading-relaxed">
              All video uploads must be approved by an administrator or moderator. Your
              video will not be visible until it is approved. By submitting a video you
              confirm that it meets the{' '}
              <a
                href="https://dsp.cafe/guidelines.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: 'var(--brand-primary)' }}
              >
                community guidelines
              </a>
              . Everyone included in a personally recorded video must agree to be filmed.
            </p>
          </div>
        )}

        {/* Submit */}
        {submitError && (
          <p className="text-sm text-center px-4" style={{ color: '#F87171' }}>{submitError}</p>
        )}

        <button
          onClick={isUpload ? handleUploadSubmit : handleSubmit}
          disabled={!canSubmit}
          className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100 flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {phase || 'Submitting...'}
            </>
          ) : (
            isUpload ? 'Upload for Review' : 'Submit for Review'
          )}
        </button>

        <p className="text-xs text-center text-tertiary pb-2">
          Submissions are reviewed by community moderators before appearing in the library.
        </p>
      </div>

      {showVerifyModal && <VerifyModal onClose={() => setShowVerifyModal(false)} />}
    </div>
  );
};

export default SubmitVideoScreen;
